import type { JsonValue } from '@/types/json.types'
import axios from 'axios'
import { apiClient, toServiceError, type ManualCommandConfig } from '@/services/http/client'
import { AccountCommandError } from '@/services/vault/accountEnrollment'
import { firmRequestReply } from '@/services/vault/firmRegistration'

export interface PlatformTenant {
  tenant_id: string
  tenant_code: string
  tenant_name: string
  tenant_type: string
  status: string
  firm_email?: string
  firm_phone?: string
  created_at: string | null
  updated_at: string | null
  archived_at?: string
  subscription?: {
    plan_code: string
    status: string
    billing_cycle?: string
    starts_at: string | null
    expires_at?: string
    max_users?: number
    max_clients?: number
  }
}
export interface CreatePlatformTenant {
  request_id: string
  command_id: string
  bootstrap_secret: string
  firm_name: string
  tenant_type: string
  plan_code: string
  owner_email: string
  owner_name: string
  firm_email?: string
  firm_phone?: string
  firm_gstin?: string
  firm_pan?: string
}
export interface PendingFirmRegistration {
  request_id: string
  command_id: string
  tenant_id: string
  tenant_code: string
  status: string
}
export interface PlatformUser {
  id: string
  username?: string
  email?: string
  full_name?: string
  user_category: string
  tenant_id?: string
  is_active: boolean | null
  is_verified: boolean | null
  roles: string[]
  created_at: string | null
  updated_at: string | null
}
export interface CreatePlatformUser {
  username: string
  email: string
  password: string
  full_name?: string
  tenant_id?: string
  role: string
  is_verified: boolean
}
export interface PlatformUserSession {
  session_id: string
  ip_address?: string
  user_agent?: string
  created_at: string | null
  expires_at: string
  last_activity_at?: string
}
export interface PlatformAuditEvent {
  id: string
  actor_id?: string
  target_tenant_id?: string
  action: string
  resource_type?: string
  resource_id?: string
  details: JsonValue
  success: boolean | null
  created_at: string | null
}
export interface DeletionRecord {
  id: string
  tenant_id: string
  source_service: string
  entity_type: string
  entity_id: string
  display_label: string
  deletion_batch_id: string
  deleted_by: string
  deletion_reason: string
  deleted_at: string
  purge_after: string
  status: string
  hold_count: number
  purge_eligible: boolean
  last_error?: string
}
export interface DeletionSummary {
  total: number
  restorable: number
  purge_eligible: number
  on_hold: number
  requested: number
  failed: number
  by_service: Record<string, number>
  by_entity_type: Record<string, number>
}
export interface PurgePreflight {
  deletion_id: string
  eligible: boolean
  retention_elapsed: boolean
  active_holds: number
  deletion_enabled: boolean
  status: string
  blockers: string[]
}
export interface Page<T> {
  items: T[]
  total: number
  page: number
  page_size: number
  pages?: number
}
export interface MonitoringImage {
  name?: string
  image: string
  digest?: string
}
export interface MonitoringObservation {
  service: { id: string; display_name: string; owner: string; dependencies: string[] }
  status: 'healthy' | 'degraded' | 'unavailable'
  observed_at: string
  stale: boolean
  kubernetes: {
    status: string
    release_state?: string
    workload?: {
      kind: string
      desired: number
      ready: number
      updated: number
      unavailable: number
    }
    pods?: { total: number; ready: number; restarts: number }
    images?: MonitoringImage[]
    warnings?: Array<{ reason: string; message: string }>
  }
  application: {
    status: string
    http_status?: number
    latency_ms?: number
    details?: Record<string, unknown>
  }
  errors: Array<{ source: string; message: string }>
  last_snapshot?: { status: string; observed_at: string; stale: boolean; correlation_id?: string }
}
export interface MonitoringPage {
  items: MonitoringObservation[]
  total: number
}
export interface MonitoringSummary extends MonitoringPage {
  counts: { healthy: number; degraded: number; unavailable: number }
  observed_at: string
}
export type OperationState =
  | 'requested'
  | 'pending_approval'
  | 'approved'
  | 'preflight'
  | 'executing'
  | 'observing'
  | 'succeeded'
  | 'failed'
  | 'unknown'
  | 'rejected'
export interface PlatformOperation {
  id: string
  service_id: string
  action_type: 'restart'
  state: OperationState
  reason: string
  ticket: string
  actor_id: string
  approver_id?: string
  environment: string
  correlation_id?: string
  pre_state?: Record<string, unknown>
  result?: Record<string, unknown>
  error_message?: string
  created_at: string
  updated_at: string
  completed_at?: string
}
export type SupportPermission = 'txh.support.data.read' | 'txh.support.data.write'
export type SupportSessionState =
  | 'requested'
  | 'approved'
  | 'active'
  | 'rejected'
  | 'revoked'
  | 'expired'
export interface SupportSession {
  id: string
  target_tenant_id: string
  requester_actor_id: string
  case_ticket: string
  reason: string
  requested_permissions: SupportPermission[]
  duration_minutes: number
  status: SupportSessionState
  approval_required: boolean
  approved_by_actor_id?: string
  approved_at?: string
  activated_at?: string
  expires_at?: string
  request_expires_at: string
  revoked_at?: string
  revocation_reason?: string
  created_at: string
  updated_at: string
}
export interface SupportTokenResponse {
  support_session: SupportSession
  access_token: string
  token_type: 'bearer'
  refresh_token: null
}
export interface MfaMethod {
  method: string
  enabled: boolean
  verified: boolean
  verified_at?: string
}
export interface TotpSetup {
  secret: string
  qr_code_url: string
  backup_codes: string[]
}

async function call<T>(request: Promise<{ data: T }>): Promise<T> {
  try {
    return (await request).data
  } catch (error) {
    throw toServiceError(error)
  }
}

export const platformApi = {
  listTenants: (params: Record<string, unknown> = {}) =>
    call<Page<PlatformTenant>>(apiClient.get('/platform/tenants', { params })),
  createTenant: async (body: CreatePlatformTenant): Promise<PendingFirmRegistration> => {
    let value: unknown
    try {
      const config: ManualCommandConfig = { manualCommand: true }
      value = (await apiClient.post('/platform/tenants', body, config)).data
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined
      throw new AccountCommandError(
        'Firm registration could not be confirmed. Keep this dialog open and retry its original request.',
        !status || status >= 500,
        status
      )
    }
    if (!firmRequestReply(body)(value))
      throw new AccountCommandError(
        'The response did not match this firm registration. Retry its original request.',
        true
      )
    return value
  },
  getTenant: (id: string) => call<PlatformTenant>(apiClient.get(`/platform/tenants/${id}`)),
  updateTenant: (id: string, body: Record<string, unknown>) =>
    call<PlatformTenant>(apiClient.patch(`/platform/tenants/${id}`, body)),
  archiveTenant: (id: string) => call<PlatformTenant>(apiClient.delete(`/platform/tenants/${id}`)),
  activateTrial: (id: string) =>
    call<PlatformTenant>(apiClient.post(`/platform/tenants/${id}/activate-trial`)),
  deleteTenantPermanently: (id: string) =>
    call<{ deleted: boolean }>(
      apiClient.delete(`/platform/tenants/${id}`, { params: { permanent: true } })
    ),
  deleteUser: (id: string, reason = 'Platform administrator requested deletion') =>
    call<{ deleted: boolean }>(
      apiClient.delete(`/platform/users/${id}`, { headers: { 'X-Deletion-Reason': reason } })
    ),
  listUsers: (params: Record<string, unknown> = {}) =>
    call<Page<PlatformUser>>(apiClient.get('/platform/users', { params })),
  createUser: (body: CreatePlatformUser) =>
    call<PlatformUser>(apiClient.post('/platform/users', body)),
  getUser: (id: string) => call<PlatformUser>(apiClient.get(`/platform/users/${id}`)),
  updateUser: (id: string, body: Record<string, unknown>) =>
    call<PlatformUser>(apiClient.patch(`/platform/users/${id}`, body)),
  assignRole: (id: string, role: string) =>
    call<PlatformUser>(apiClient.put(`/platform/users/${id}/role`, { role })),
  deactivateUser: (id: string) =>
    call<PlatformUser>(apiClient.post(`/platform/users/${id}/deactivate`)),
  listUserSessions: (id: string) =>
    call<{ items: PlatformUserSession[]; total: number }>(
      apiClient.get(`/platform/users/${id}/sessions`)
    ),
  revokeSessions: async (id: string) => {
    await call(apiClient.delete(`/platform/users/${id}/sessions`))
  },
  listAudit: (params: Record<string, unknown> = {}) =>
    call<Page<PlatformAuditEvent>>(apiClient.get('/platform/audit', { params })),
  listDeletions: (params: Record<string, unknown> = {}) =>
    call<Page<DeletionRecord>>(apiClient.get('/platform/deletions', { params })),
  deletionSummary: () => call<DeletionSummary>(apiClient.get('/platform/deletions/summary')),
  deletionPreflight: (id: string) =>
    call<PurgePreflight>(apiClient.get(`/platform/deletions/${encodeURIComponent(id)}/preflight`)),
  purgeDeletion: (
    id: string,
    body: { reason: string; ticket: string; confirmation: string },
    idempotencyKey: string
  ) =>
    call<{ id: string; state: string }>(
      apiClient.post(`/platform/deletions/${encodeURIComponent(id)}/purge`, body, {
        headers: { 'Idempotency-Key': idempotencyKey },
      })
    ),
  createPlatformHold: (id: string, reason: string) =>
    call<{ id: string }>(
      apiClient.post(`/platform/deletions/${encodeURIComponent(id)}/holds`, { reason })
    ),
  mfaMethods: () => call<MfaMethod[]>(apiClient.get('/mfa/methods')),
  setupTotp: () => call<TotpSetup>(apiClient.post('/mfa/totp/setup')),
  verifyTotpSetup: (code: string) =>
    call<{ verified: boolean; verified_at: string }>(apiClient.post('/mfa/totp/verify', { code })),
  verifyMfa: (code: string) =>
    call<{ verified: boolean; verified_at: string }>(apiClient.post('/mfa/verify', { code })),
  monitoringSummary: () => call<MonitoringSummary>(apiClient.get('/monitoring/summary')),
  monitoringServices: () => call<MonitoringPage>(apiClient.get('/monitoring/services')),
  monitoringService: (id: string) =>
    call<MonitoringObservation>(apiClient.get(`/monitoring/services/${encodeURIComponent(id)}`)),
  executeHealthCheck: (id: string) =>
    call<Record<string, unknown>>(
      apiClient.post(`/monitoring/services/${encodeURIComponent(id)}/health-check`)
    ),
  monitoringDependencies: () =>
    call<{ items: Array<{ service: string; depends_on: string[] }> }>(
      apiClient.get('/monitoring/dependencies')
    ),
  monitoringVersions: () =>
    call<{ items: Array<{ service: string; images: MonitoringImage[]; release_state: string }> }>(
      apiClient.get('/monitoring/versions')
    ),
  restartService: (id: string, body: { reason: string; ticket: string }, idempotencyKey: string) =>
    call<PlatformOperation>(
      apiClient.post(`/monitoring/services/${encodeURIComponent(id)}/restart`, body, {
        headers: { 'Idempotency-Key': idempotencyKey },
      })
    ),
  listOperations: (limit = 100) =>
    call<{ items: PlatformOperation[]; total: number }>(
      apiClient.get('/monitoring/operations', { params: { limit } })
    ),
  getOperation: (id: string) =>
    call<PlatformOperation>(apiClient.get(`/monitoring/operations/${encodeURIComponent(id)}`)),
  decideOperation: (id: string, approved: boolean, reason?: string) =>
    call<PlatformOperation>(
      apiClient.post(`/monitoring/operations/${encodeURIComponent(id)}/approval`, {
        approved,
        reason,
      })
    ),
  listSupportSessions: (params: Record<string, unknown> = {}) =>
    call<Page<SupportSession>>(apiClient.get('/platform/support-sessions', { params })),
  createSupportSession: (body: {
    target_tenant_id: string
    ticket: string
    reason: string
    duration_minutes: number
    permissions: SupportPermission[]
  }) => call<SupportSession>(apiClient.post('/platform/support-sessions', body)),
  decideSupportSession: (id: string, approved: boolean, reason?: string) =>
    call<SupportSession>(
      apiClient.post(`/platform/support-sessions/${encodeURIComponent(id)}/approval`, {
        approved,
        reason,
      })
    ),
  activateSupportSession: (id: string) =>
    call<SupportTokenResponse>(
      apiClient.post(`/platform/support-sessions/${encodeURIComponent(id)}/token`)
    ),
  revokeSupportSession: (id: string, reason: string) =>
    call<SupportSession>(
      apiClient.post(`/platform/support-sessions/${encodeURIComponent(id)}/revoke`, { reason })
    ),
}

export const tenantDeletionApi = {
  list: (params: Record<string, unknown> = {}) =>
    call<Page<DeletionRecord>>(apiClient.get('/deletions', { params })),
  summary: () => call<DeletionSummary>(apiClient.get('/deletions/summary')),
  restore: (id: string, reason: string) =>
    call<DeletionRecord>(
      apiClient.post(`/deletions/${encodeURIComponent(id)}/restore`, { reason })
    ),
  requestPurge: (id: string, reason: string) =>
    call<DeletionRecord>(
      apiClient.post(`/deletions/${encodeURIComponent(id)}/purge-request`, { reason })
    ),
  createHold: (id: string, reason: string) =>
    call<{ id: string }>(apiClient.post(`/deletions/${encodeURIComponent(id)}/holds`, { reason })),
}
